import { makeAutoObservable } from 'mobx';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { url } from '@monorepo/tools/src/lib/types/url';
import { AccountsModel, IAccount } from './account.model';
import { AdvertiserModel } from './advertiser.model';
import { randomNumber } from '@monorepo/tools/src/lib/utils/number';
import { CampaignCreativeModel, ICampaignCreative } from './campaign-creative.model';
import { Status } from '../enums/status';

export type IDestinationCreative = ICampaignCreative;
export type DestinationCreativeModel = CampaignCreativeModel;

export interface IDestinationEditFormCreative {
	id?: id;
	status?: Status;
	weight?: number;
	creative: { id: id };
}

export interface IDestinationPricing {
	id?: id;
	geo: string;
	default_payout: number;
	devId?: number;
}

export interface IDestinationAdvertiser {
	id?: id;
	name?: string;
	user_cap_enabled?: boolean;
}

export interface IDestination {
	id?: id;
	name?: string;
	dest_url: url;
	yid?: string;
	account?: IAccount;
	advertiser?: IDestinationAdvertiser;
	destination_prices?: IDestinationPricing[];
	creatives?: IDestinationCreative[];
}

export interface IDestinationCreateAccount {
	id: id;
	name?: string;
	use_internal_pricing?: boolean;
}

export interface IDestinationCreateForm {
	name: string;
	advertiser: { id: id };
	account: IDestinationCreateAccount;
	dest_url: url;
	yid?: string;
	destination_prices?: IDestinationPricing[];
}

export interface IDestinationEditForm {
	name: string;
	dest_url: url;
	yid?: string;
	destination_prices?: IDestinationPricing[];
	creatives?: IDestinationEditFormCreative[];
}

export class DestinationPricingModel implements IDestinationPricing {
	id?: id;
	geo: string;
	default_payout: number;
	devId?: number;

	constructor(props?: Partial<IDestinationPricing>) {
		this.devId = props?.devId ?? parseInt(randomNumber(111, 999, 0));
		this.id = props?.id;
		this.geo = props?.geo ?? '';
		this.default_payout = props?.default_payout ?? 0;
		makeAutoObservable(this);
	}

	getGeo(): string {
		return this.geo;
	}

	setGeo(geo: string) {
		this.geo = geo;
	}

	getDefaultPayout(): number {
		return this.default_payout;
	}

	setDefaultPayout(default_payout: number) {
		this.default_payout = default_payout;
	}

	getDevId(): number | undefined {
		return this.devId;
	}
	setDevId(devId: number) {
		this.devId = devId;
	}

	toObject(): IDestinationPricing {
		return {
			id: this.id,
			geo: this.geo,
			default_payout: this.default_payout,
		};
	}
}

export class DestinationModel implements IDestination {
	id?: id;
	name?: string;
	dest_url: url;
	yid?: string;
	account?: AccountsModel;
	advertiser?: AdvertiserModel;
	destination_prices?: DestinationPricingModel[];
	creatives?: DestinationCreativeModel[];

	constructor(destination?: IDestination) {
		this.id = destination?.id;
		this.name = destination?.name;
		this.dest_url = destination?.dest_url ?? '';
		this.yid = destination?.yid ?? '';
		this.account = destination?.account ? new AccountsModel(destination.account) : undefined;
		this.advertiser = destination?.advertiser ? new AdvertiserModel(destination.advertiser) : undefined;
		this.destination_prices = destination?.destination_prices?.map(pricing => new DestinationPricingModel(pricing)) ?? [];
		this.creatives = destination?.creatives?.map(creative => new CampaignCreativeModel(creative)) ?? [];
		makeAutoObservable(this);
	}

	public getId(): id | undefined {
		return this.id;
	}

	public getName(): string | undefined {
		return this.name;
	}

	public setName(name: string): void {
		this.name = name;
	}

	public getDestUrl(): url {
		return this.dest_url;
	}

	public setDestUrl(dest_url: url): void {
		this.dest_url = dest_url;
	}

	public getYid(): string | undefined {
		return this.yid;
	}

	public setYid(yid: string): void {
		this.yid = yid;
	}

	public getAccount(): AccountsModel | undefined {
		return this.account;
	}

	public setAccount(account: AccountsModel | undefined): void {
		this.account = account;
	}

	public getAdvertiser(): AdvertiserModel | undefined {
		return this.advertiser;
	}

	public setAdvertiser(advertiser: AdvertiserModel | undefined): void {
		this.advertiser = advertiser;
	}

	public getDestinationPricing(): DestinationPricingModel[] {
		return this.destination_prices ?? [];
	}

	public setDestinationPricing(pricing: DestinationPricingModel[]): void {
		this.destination_prices = pricing;
	}

	public addPricingRow(): void {
		this.destination_prices = [...(this.destination_prices ?? []), new DestinationPricingModel()];
	}

	public removePricingRow(rowId: id): void {
		this.destination_prices = this.destination_prices?.filter(row => row.id !== rowId);
	}

	public usesExternalPricing(): boolean {
		return this.account?.getUseInternalPricing() === false;
	}

	public getCreatives(): DestinationCreativeModel[] {
		return this.creatives ?? [];
	}

	public setCreatives(creatives: DestinationCreativeModel[]): void {
		this.creatives = creatives;
	}
}
